import type { TFunction } from "i18next";
import type { DefinitionTooltipInfo } from "src/components/common";

/**
 * Resolves the CLTV dashboard's (?) definition-tooltip copy from the locale files, mirroring
 * `organicMediaDefinitions.ts`. Every bundle lives under `cltv.<group>Help.<leaf>` with a
 * `description` and optional `formula`/`note`, and an absent bundle resolves to `undefined`
 * so the caller can skip rendering the tooltip entirely.
 */
function readHelpBundle(
  t: TFunction,
  base: string,
  leaf: string
): DefinitionTooltipInfo | undefined {
  const description = t(`${base}.${leaf}.description`, { defaultValue: "" });
  if (!description) {
    return undefined;
  }

  const formula = t(`${base}.${leaf}.formula`, { defaultValue: "" });
  const note = t(`${base}.${leaf}.note`, { defaultValue: "" });

  return {
    description,
    ...(formula ? { formula } : {}),
    ...(note ? { note } : {}),
  };
}

/** Map an API id / segment name to the stable snake_case leaf used in the locale files. */
export function cltvIdSlug(id: string): string {
  return (
    id
      .trim()
      .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
      .toLowerCase()
      .replace(/-/g, "_")
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "") || "unknown"
  );
}

export function cltvSegmentNameSlug(name: string): string {
  return (
    name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "") || "unknown"
  );
}

/** KPI-card help, addressed by the slugified card id (`average_cltv`, `churn_risk`, …). */
export function getCltvKpiDefinition(id: string, t: TFunction) {
  return readHelpBundle(t, "cltv.kpiHelp", cltvIdSlug(id));
}

/** Predictive-insight help, addressed by the slugified insight id. */
export function getCltvInsightDefinition(id: string, t: TFunction) {
  return readHelpBundle(t, "cltv.insightHelp", cltvIdSlug(id));
}

export type CltvColumnHelpKey =
  | "profile"
  | "totalSpend"
  | "totalOrders"
  | "predictedSpend"
  | "predictedOrders"
  | "segments";

/** Customer-table column help. Leaf keys match the table's sort keys. */
export function getCltvColumnDefinition(key: CltvColumnHelpKey, t: TFunction) {
  return readHelpBundle(t, "cltv.columnHelp", key);
}

export type CltvSectionHelpKey =
  | "segmentDistribution"
  | "predictiveInsights"
  | "spendingPatterns"
  | "segmentEvolution"
  | "customerTable";

export function getCltvSectionDefinition(key: CltvSectionHelpKey, t: TFunction) {
  // The segment-distribution panel and the customer table's Segments column describe the same
  // classification, so they deliberately share one explanation instead of duplicating the copy.
  if (key === "segmentDistribution") {
    return getCltvColumnDefinition("segments", t);
  }
  return readHelpBundle(t, "cltv.sectionHelp", key);
}

/** Model risk bands. The API returns `tier` already localized, so it carries no stable code. */
export type CltvRiskTierCode = "high" | "medium" | "low";

/**
 * Recovers the risk band from the API's localized `tier` label so ordering and colour stay
 * correct in every language. Matches the English label first, then the localized label this
 * app would render for that band. Returns `null` for anything unrecognized, in which case
 * callers keep the API's own ordering and use a neutral colour.
 *
 * TODO(backend): `predictiveInsights[].tiers[]` should expose a stable `tier_code`
 * (`high` | `medium` | `low`) so this label matching can be dropped.
 */
export function cltvRiskTierCode(tier: string, t: TFunction): CltvRiskTierCode | null {
  const slug = cltvSegmentNameSlug(tier);
  if (slug.startsWith("high")) {
    return "high";
  }
  if (slug.startsWith("medium")) {
    return "medium";
  }
  if (slug.startsWith("low")) {
    return "low";
  }

  const normalized = tier.trim();
  const codes: CltvRiskTierCode[] = ["high", "medium", "low"];
  return (
    codes.find(code => t(`cltv.riskTiers.${code}`, { defaultValue: "" }) === normalized) ?? null
  );
}
